/**
 * Browser side of the sign-up source (see signupSource.ts for the rules).
 *
 * captureSignupSourceFromUrl() runs once when the app loads (src/main.tsx), so a ?src= tag on ANY
 * landing URL (/register/step1, /employer-registration, /institute-registration, /signup, ...) is
 * remembered for the rest of this browser tab. That is what carries it through a Google sign-in:
 * the popup and the role-selection page lose the query string, but sessionStorage survives.
 * signupSourceField() is spread into the payload that CREATES profiles/{uid}.
 */
import { SIGNUP_SOURCE_DIRECT, sanitizeSignupSource } from './signupSource';

export const SIGNUP_SOURCE_STORAGE_KEY = 'arevinaSignupSource';

/** Remembers ?src= from the given query string (defaults to the current URL). Latest tag wins. */
export function captureSignupSourceFromUrl(search?: string): void {
  try {
    const params = new URLSearchParams(search ?? window.location.search);
    const raw = params.get('src');
    if (raw === null || !raw.trim()) return;
    sessionStorage.setItem(SIGNUP_SOURCE_STORAGE_KEY, sanitizeSignupSource(raw));
  } catch {
    // Storage blocked (private mode etc.): the sign-up is simply recorded as "direct".
  }
}

/** The remembered source for this tab, already sanitized; "direct" when there is none. */
export function readSignupSource(): string {
  try {
    const stored = sessionStorage.getItem(SIGNUP_SOURCE_STORAGE_KEY);
    return stored ? sanitizeSignupSource(stored) : SIGNUP_SOURCE_DIRECT;
  } catch {
    return SIGNUP_SOURCE_DIRECT;
  }
}

/** `{ signupSource }` for a profile-create payload. */
export function signupSourceField(): { signupSource: string } {
  return { signupSource: readSignupSource() };
}
